// @ts-ignore
import { jStat } from 'jstat';
import { DerivationMetrics } from 'modules/experiments/types';
import { NA } from 'modules/experiments/constants';

import { getZScore } from './statisticalDerivations';

export function truncateTwoDecimalPlaces(num: number): number {
  return Math.floor(num * 100) / 100.0;
}

export function truncateToNonZero(num: number): number {
  const log10 = num ? Math.floor(Math.log10(num)) : 0;
  const div = log10 < 0 ? 10 ** (1 - log10) : 100;

  return Math.floor(num * div) / div;
}

/**
 *
 * Calculates the "lift" in conversion rate to help users determine if campaigns are worth switching to
 * Every campaign will have key value pair experimentWinningCriteria and experimentSends in data model
 * Formula = [experimentWinningCriteria(variant)/experimentSends(variant)]-[experimentWinningCriteria(control)/experimentSends(control)] * 100
 *
 * @param {object.variantSend} - experimentSends value of object in the variations array (See analytics data model)
 * @param {object.variantExperimentWinningCriteria} - experimentWinningCriteria value of object in the variations array (See analytics data model)
 * @param {object.controlExperimentSends} - experimentSends value of object in the variations array (See analytics data model)
 * @param {object.controlExperimentWinningCriteria} - experimentWinningCriteria value of object in the variations array (See analytics data model)
 * @return {string} "lift" in conversion rate
 */
export function calculateLift({
  variantSends,
  variantExperimentWinningCriteria,
  controlExperimentSends,
  controlExperimentWinningCriteria,
}: DerivationMetrics): number {
  if (variantSends > 0 && controlExperimentSends > 0) {
    const liftValue =
      (variantExperimentWinningCriteria / variantSends -
        controlExperimentWinningCriteria / controlExperimentSends) *
      100;
    return truncateTwoDecimalPlaces(liftValue);
  } else {
    return 0.0;
  }
}

/**
 * Reported as Lift ± ME where ME = z* * SE * 100
 *
 * where SE = sqrt( p_t(1 - p_t) / n_t + p_c(1 - p_c) / n_c )
 *
 * SE = standard error of the Sample Statistic
 *  pT = unique conversion rate for the test variant (as a proportion, not a %)
 *  nT = number of sends for the test variant (i.e. sample size)
 *  pC = unique conversion rate for the control (as a proportion, not a %)
 *  nC = number of sends for the control (i.e. sample size)
 *  z* is the score returned from getZScore
 *
 */
export function calculateLiftInterval({
  variantSends,
  variantExperimentWinningCriteria,
  controlExperimentSends,
  controlExperimentWinningCriteria,
}: DerivationMetrics): number {
  const pT = variantExperimentWinningCriteria / variantSends;
  const nT = variantSends;
  const pC = controlExperimentWinningCriteria / controlExperimentSends;
  const nC = controlExperimentSends;

  const SE = Math.sqrt((pT * (1 - pT)) / nT + (pC * (1 - pC)) / nC);
  const zScore = getZScore(95);
  const ME = zScore * SE * 100;
  return truncateTwoDecimalPlaces(ME);
}

/**
 * A small util method which prevents 100% or 0% over confident results
 *
 * @param confidence number
 * @returns number
 */
const ABSOLUTE_CONFIDENCE = 100;
const ZERO_CONFIDENCE = 0;
const DOUBT_FACTOR = 0.01;
export function preventOverConfidence(confidence: number) {
  switch (confidence) {
    case ABSOLUTE_CONFIDENCE:
      return confidence - DOUBT_FACTOR;

    case ZERO_CONFIDENCE:
      return confidence + DOUBT_FACTOR;

    default:
      return confidence;
  }
}

/**
 * Confidence = (1 - jStat.fn.twoSidedDifferenceOfProportions(p1, n1, p2, n2)
 *
 * jStat.fn.twoSidedDifferenceOfProportions( p1, n1, p2, n2 ) where
 * p1 = unique conversion rate (variant) [experimentWinningCriteria(variant)/experimentSends(variant)]
 * n1 = sample size (variant) [experimentSends(variant)]
 * p2 = unique conversion rate (control) [experimentWinningCriteria(control)/experimentSends(control)]
 * n2 = sample size (control)[experimentSends(control) ]
 *
 */
export function calculateConfidence({
  variantSends,
  variantExperimentWinningCriteria,
  controlExperimentSends,
  controlExperimentWinningCriteria,
}: DerivationMetrics): number | typeof NA {
  const p1 = variantExperimentWinningCriteria / variantSends;
  const n1 = variantSends;
  const p2 = controlExperimentWinningCriteria / controlExperimentSends;
  const n2 = controlExperimentSends;

  if (p1 > 1 || p2 > 1 || p1 <= 0 || p2 <= 0) {
    return NA;
  }

  const confidence = (1 - jStat.fn.twoSidedDifferenceOfProportions(p1, n1, p2, n2)) * 100;
  return truncateToNonZero(preventOverConfidence(confidence));
}
